// src/pages/info/EditHouseDetails.jsx

import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Select from 'react-select';
import CancelEditingModal from '../../../components/Modals/CancelEditingModal';
import warningIcon from '../../../icons/warning.svg';
import './EditHouseDetails.css';
import Modal from 'react-modal';
import SaveChangesModal from '../../../components/Modals/SaveChangesModal';
import API_BASE_URL from '../../../config';

Modal.setAppElement('#root');

const parseDescription = raw => {
  const parts = [];
  const regex = /(\*{2,})([\s\S]*?)\1|@@([\s\S]*?)@@/g;
  let lastIndex = 0;
  let match;

  while ((match = regex.exec(raw))) {
    if (match.index > lastIndex) {
      const txt = raw.slice(lastIndex, match.index).trim();
      if (txt) parts.push({ type: 'paragraph', text: txt });
    }
    if (match[1]) {
      parts.push({ type: 'heading', text: match[2].trim() });
    } else if (match[3]) {
      parts.push({ type: 'paragraph', text: match[3].trim() });
    }
    lastIndex = regex.lastIndex;
  }
  if (lastIndex < raw.length) {
    const txt = raw.slice(lastIndex).trim();
    if (txt) parts.push({ type: 'paragraph', text: txt });
  }
  return parts;
};

const EditHouseDetails = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const containerRef = useRef(null);

  // Ожидаем, что при навигации переданы houseData и source
  const passedHouse = location.state?.houseData || null;
  const source = location.state?.source || 'db';

  // Состояния полей, инициализируем значениями из passedHouse (может быть null)
  const [seriesOptions, setSeriesOptions] = useState([]);
  const [selectedSeries, setSelectedSeries] = useState(null);
  const [floors, setFloors] = useState(passedHouse?.floors || '');
  const [apartments, setApartments] = useState(passedHouse?.count_appartaments || '');
  const [year, setYear] = useState(passedHouse?.construction_year || '');
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxSrc, setLightboxSrc] = useState('');
  const [descriptionParts, setDescriptionParts] = useState([]);
  const [showConfirm, setShowConfirm] = useState(false);
  const [mode, setMode] = useState('goBack');

  // Для навигации через Sidebar
  const [pendingNavPath, setPendingNavPath] = useState(null);

  // Функция для сохранения и перехода к списку серий, передавая выбранную серию
  const handleGoToSeries = () => {
    if (!passedHouse || !selectedSeries) return;

    const params = new URLSearchParams();
    if (floors !== "") params.append("floors", floors);
    if (apartments !== "") params.append("count_appartaments", apartments);
    if (year !== "") params.append("construction_year", year);
    params.append("series_id", selectedSeries.value);

    fetch(
      `${API_BASE_URL}/building/update_build/${passedHouse.id}?${params.toString()}`,
      { method: "PUT" }
    )
      .then((response) => {
        if (!response.ok) return Promise.reject(`Ошибка ${response.status}`);
        navigate("/db-series", {
          state: {
            from: "editHouse",
            house: passedHouse,
            selectedId: selectedSeries.value,
            series: selectedSeries,
          },
        });
      })
      .catch((err) => {
        console.error("При сохранении дома произошла ошибка:", err);
      });
  };

  // Загрузка списка серий и установка начального selectedSeries
  useEffect(() => {
    if (!passedHouse || !passedHouse.series_id) return;

    fetch(`${API_BASE_URL}/building/series_all`)
      .then(r => {
        if (!r.ok) throw new Error(r.status);
        return r.json();
      })
      .then(data => {
        const opts = data.map(s => ({
          value: s.id,
          label: s.name,
          description: s.description,
          images: s.images
        }));
        setSeriesOptions(opts);

        const cur = opts.find(o => o.value === passedHouse.series_id);
        setSelectedSeries(cur || null);
        setDescriptionParts(parseDescription(cur?.description || ''));
      })
      .catch(console.error);
  }, [passedHouse]);

  // Если пользователь меняет selectedSeries, обновляем описание
  useEffect(() => {
    if (!selectedSeries) {
      setDescriptionParts([]);
      return;
    }
    setDescriptionParts(parseDescription(selectedSeries.description || ''));
  }, [selectedSeries]);

  // Авто-рост для textarea (если понадобятся)
  useEffect(() => {
    setTimeout(() => {
      containerRef.current
        ?.querySelectorAll('.description-textarea')
        .forEach(el => {
          el.style.height = 'auto';
          el.style.height = `${el.scrollHeight}px`;
        });
    }, 0);
  }, [descriptionParts]);

  // Сохранение и возврат к детали дома
  const handleSaveAndGoBack = () => {
    if (!passedHouse) return;
    const params = new URLSearchParams();
    if (floors !== '') params.append('floors', floors);
    if (apartments !== '') params.append('count_appartaments', apartments);
    if (year !== '') params.append('construction_year', year);
    if (selectedSeries?.value) params.append('series_id', selectedSeries.value);

    fetch(
      `${API_BASE_URL}/building/update_build/${passedHouse.id}?${params.toString()}`,
      { method: 'PUT' }
    )
      .then(r => {
        if (!r.ok) return Promise.reject(`Ошибка ${r.status}`);
        navigate(`/db-house/${passedHouse.id}`, {
          state: { source: source }
        });
      })
      .catch(err => {
        console.error('При сохранении дома произошла ошибка:', err);
      });
  };

  // Сохранение и навигация по пути из Sidebar
  const handleSaveAndNavigate = (path) => {
    if (!passedHouse) return;
    const params = new URLSearchParams();
    if (floors !== '') params.append('floors', floors);
    if (apartments !== '') params.append('count_appartaments', apartments);
    if (year !== '') params.append('construction_year', year);
    if (selectedSeries?.value) params.append('series_id', selectedSeries.value);

    fetch(
      `${API_BASE_URL}/building/update_build/${passedHouse.id}?${params.toString()}`,
      { method: 'PUT' }
    )
      .then(r => {
        if (!r.ok) return Promise.reject(`Ошибка ${r.status}`);
        navigate(path);
      })
      .catch(err => {
        console.error('При сохранении дома произошла ошибка:', err);
      });
  };

  // Слушатель попыток навигации из Sidebar
  useEffect(() => {
    const onNavigateAttempt = (e) => {
      setPendingNavPath(e.detail);
      setShowConfirm(true);
    };
    window.addEventListener('navigateAttempt', onNavigateAttempt);
    return () => {
      window.removeEventListener('navigateAttempt', onNavigateAttempt);
    };
  }, []);

  // Если passedHouse нет (можно вернуть null)
  if (!passedHouse) {
    return null;
  }

  return (
    <div className="edit-house-page">
      <div className="edit-header-home">
        <button className="back-btn-home" onClick={() => setShowCancelModal(true)}>
          <span className="back-icon-home">⌄</span>
        </button>
        <h1 className="edit-title">
          Редактирование записи:&nbsp;
          <span className="edit-address">{passedHouse.address}</span>
        </h1>
        <div className="edit-actions">
          <button
            className="primary-btn cancel"
            onClick={() => setShowCancelModal(true)}
          >
            Отменить
          </button>
          <button
            className="primary-btn save"
            onClick={handleSaveAndGoBack}
          >
            Сохранить
          </button>
        </div>
      </div>

      {showCancelModal && (
        <CancelEditingModal
          onClose={() => setShowCancelModal(false)}
          onConfirm={() => navigate(-1)}
        />
      )}

      <div className="edit-house-body" ref={containerRef}>
        <div className="edit-house-left">
          <div className="edit-house-table-container">
            <div className="edit-house-table-wrapper">
              <table className="edit-house-table">
                <tbody>
                  {/* Серия дома */}
                  <tr className="edit-house-table__row">
                    <td className="edit-house-table__cell edit-house-table__cell--label">
                      Серия дома
                    </td>
                    <td className="edit-house-table__cell edit-house-table__cell--value">
                      <Select
                        options={seriesOptions}
                        value={selectedSeries}
                        onChange={setSelectedSeries}
                        className="edit-series-select"
                        classNamePrefix="edit-series"
                        isSearchable
                        menuPortalTarget={document.body}
                        menuPosition="absolute"
                        styles={{
                          control: base => ({
                            ...base,
                            backgroundColor: '#15171D',
                            borderColor: '#3B3B40',
                            borderRadius: '4px',
                            minHeight: '36px'
                          }),
                          singleValue: base => ({ ...base, color: '#e5e7eb' }),
                          placeholder: base => ({ ...base, color: '#8b919d' }),
                          menu: base => ({
                            ...base,
                            backgroundColor: '#2a2c33',
                            borderColor: '#3B3B40',
                            marginTop: '4px'
                          }),
                          option: (base, state) => ({
                            ...base,
                            backgroundColor: state.isFocused
                              ? '#3B3B40'
                              : '#2a2c33',
                            color: '#e5e7eb'
                          }),
                          menuPortal: base => ({ ...base, zIndex: 9999 })
                        }}
                      />
                    </td>
                  </tr>

                  {/* Количество этажей */}
                  <tr className="edit-house-table__row">
                    <td className="edit-house-table__cell edit-house-table__cell--label">
                      Количество этажей
                    </td>
                    <td className="edit-house-table__cell edit-house-table__cell--value">
                      <input
                        className="edit-house-input"
                        type="text"
                        value={floors}
                        onKeyDown={e => {
                          const allowedKeys = [
                            'Backspace',
                            'Delete',
                            'ArrowLeft',
                            'ArrowRight',
                            'Tab'
                          ];
                          if (
                            !allowedKeys.includes(e.key) &&
                            !e.key.match(/^[0-9]$/)
                          ) {
                            e.preventDefault();
                          }
                        }}
                        onChange={e => {
                          const onlyDigits = e.target.value.replace(/\D+/g, '');
                          setFloors(onlyDigits);
                        }}
                        inputMode="numeric"
                        pattern="\d*"
                      />
                    </td>
                  </tr>

                  {/* Количество квартир */}
                  <tr className="edit-house-table__row">
                    <td className="edit-house-table__cell edit-house-table__cell--label">
                      Количество квартир
                    </td>
                    <td className="edit-house-table__cell edit-house-table__cell--value">
                      <input
                        className="edit-house-input"
                        type="text"
                        value={apartments}
                        onKeyDown={e => {
                          const allowedKeys = [
                            'Backspace',
                            'Delete',
                            'ArrowLeft',
                            'ArrowRight',
                            'Tab'
                          ];
                          if (
                            !allowedKeys.includes(e.key) &&
                            !e.key.match(/^[0-9]$/)
                          ) {
                            e.preventDefault();
                          }
                        }}
                        onChange={e => {
                          const onlyDigits = e.target.value.replace(/\D+/g, '');
                          setApartments(onlyDigits);
                        }}
                        inputMode="numeric"
                        pattern="\d*"
                      />
                    </td>
                  </tr>

                  {/* Год постройки */}
                  <tr className="edit-house-table__row">
                    <td className="edit-house-table__cell edit-house-table__cell--label">
                      Год постройки
                    </td>
                    <td className="edit-house-table__cell edit-house-table__cell--value">
                      <input
                        className="edit-house-input"
                        type="text"
                        value={year}
                        onKeyDown={e => {
                          const allowedKeys = [
                            'Backspace',
                            'Delete',
                            'ArrowLeft',
                            'ArrowRight',
                            'Tab'
                          ];
                          if (
                            !allowedKeys.includes(e.key) &&
                            !e.key.match(/^[0-9]$/)
                          ) {
                            e.preventDefault();
                          }
                        }}
                        onChange={e => {
                          const onlyDigits = e.target.value.replace(/\D+/g, '');
                          setYear(onlyDigits);
                        }}
                        inputMode="numeric"
                        pattern="\d*"
                      />
                    </td>
                  </tr>

                  {/* Описание серии (только для просмотра) */}
                  <tr className="edit-house-table__row">
                    <td className="edit-house-table__cell edit-house-table__cell--label">
                      Описание серии
                    </td>
                    <td className="edit-house-table__cell edit-house-table__cell--desc edit-house-desc-cell">
                      <div className="HD-text prokrutka">
                        {descriptionParts.map((part, idx) =>
                          part.type === 'heading' ? (
                            <p key={idx}>
                              <strong>{part.text}</strong>
                            </p>
                          ) : (
                            <p key={idx}>{part.text}</p>
                          )
                        )}
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div className="edit-divider" />

        <div className="edit-house-right nh">
          <div className="notice-block">
            <img
              src={warningIcon}
              alt="Warning"
              className="notice-block__icon"
            />
            <div className="notice-block__text">
              Редактирование чертежей и описания невозможно в данном режиме!<br />
              Для редактирования серии перейдите:&nbsp;
              <span
                className="notice-block__link"
                onClick={() => {
                  setMode('goToSeries');
                  setShowConfirm(true);
                }}
              >
                Справочники › БД серий
              </span>
            </div>
          </div>

          <div className="collector-photo-gallery nh">
            {selectedSeries?.images?.length > 0 ? (
              selectedSeries.images.map((img, i) => (
                <div
                  key={i}
                  className="collector-photo-item nh"
                  onClick={() => {
                    setLightboxSrc(img.url);
                    setLightboxOpen(true);
                  }}
                  style={{ cursor: 'zoom-in' }}
                >
                  <img src={img.url} alt={`Чертёж ${i + 1}`} />
                </div>
              ))
            ) : (
              <div className="no-drawings-message">
                Чертежей у этой серии нет
              </div>
            )}
          </div>
        </div>

        <Modal
          isOpen={lightboxOpen}
          onRequestClose={() => setLightboxOpen(false)}
          className="lightbox-modal"
          overlayClassName="lightbox-overlay"
        >
          <img
            src={lightboxSrc}
            alt="Чертёж во весь экран"
            className="lightbox-image"
            onClick={() => setLightboxOpen(false)}
          />
        </Modal>
      </div>

      {showConfirm && pendingNavPath && (
        <SaveChangesModal
          onClose={() => {
            setShowConfirm(false);
            setPendingNavPath(null);
          }}
          onConfirm={() => {
            setShowConfirm(false);
            handleSaveAndNavigate(pendingNavPath);
            setPendingNavPath(null);
          }}
        />
      )}

      {showConfirm && !pendingNavPath && mode === 'goBack' && (
        <SaveChangesModal
          onClose={() => setShowConfirm(false)}
          onConfirm={() => {
            setShowConfirm(false);
            handleSaveAndGoBack();
          }}
        />
      )}

      {showConfirm && !pendingNavPath && mode === 'goToSeries' && (
        <SaveChangesModal
          onClose={() => setShowConfirm(false)}
          onConfirm={() => {
            setShowConfirm(false);
            handleGoToSeries();
          }}
        />
      )}
    </div>
  );
};

export default EditHouseDetails;
